import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export interface WorkflowStep {
  id: number;
  workflow_id: number;
  step_order: number;
  name: string;
  approver_role: string;
  sla_hours: number;
  escalate_after_hours: number;
  created_at: string;
}

export interface ApprovalWorkflow {
  id: number;
  name: string;
  description: string | null;
  min_seats: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  steps: WorkflowStep[];
}

export interface ApprovalRequest {
  id: number;
  application_id: number;
  workflow_id: number | null;
  current_step: number;
  status: string;
  created_by: string | null;
  step_started_at: string | null;
  reminded_at: string | null;
  escalated_at: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface ApprovalDecision {
  id: number;
  request_id: number;
  step_order: number;
  step_name: string | null;
  approver_id: string | null;
  approver_name: string | null;
  decision: string;
  note: string | null;
  created_at: string;
}

export interface ApprovalApplication {
  id: number;
  user_id: string | null;
  applicant_name: string;
  email: string | null;
  company: string | null;
  product_name: string;
  product_version: string | null;
  edition: string;
  seat_count: number;
  machine_code: string | null;
  status: string;
  created_at: string;
  renew_license_id: number | null;
}

export interface WorkflowStepInput {
  name: string;
  approverRole: "staff" | "admin";
  slaHours: number;
  escalateHours: number;
}

export interface WorkflowInput {
  workflowId?: number;
  name: string;
  description: string;
  minSeats: number;
  enabled: boolean;
  steps: WorkflowStepInput[];
}

async function invokeApprovals<T>(payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-approvals", { body: payload });
  if (error) {
    let message = error.message || "操作失败，请稍后重试";
    try {
      const context = (error as { context?: Response }).context;
      if (context) {
        const parsed = await context.json();
        if (parsed?.error) message = parsed.error;
      }
    } catch {
      // keep original message
    }
    throw new Error(message);
  }
  const result = data as { error?: string } | null;
  if (result?.error) throw new Error(result.error);
  return data as T;
}

interface UseApprovalsResult {
  workflows: ApprovalWorkflow[];
  requests: ApprovalRequest[];
  decisions: ApprovalDecision[];
  applications: ApprovalApplication[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  saveWorkflow: (input: WorkflowInput) => Promise<void>;
  deleteWorkflow: (workflowId: number) => Promise<void>;
  startRequest: (applicationId: number) => Promise<void>;
  decide: (requestId: number, decision: "approved" | "rejected", note: string) => Promise<void>;
  checkTimeouts: () => Promise<{ reminded: number; escalated: number }>;
}

// 审批流与多级审批请求：工作流配置、申请审批请求、逐级决策与流转，全部走 admin-approvals。
export function useApprovals(): UseApprovalsResult {
  const { user, loading: authLoading } = useCurrentUser();
  const [workflows, setWorkflows] = useState<ApprovalWorkflow[]>([]);
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [decisions, setDecisions] = useState<ApprovalDecision[]>([]);
  const [applications, setApplications] = useState<ApprovalApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setWorkflows([]);
      setRequests([]);
      setDecisions([]);
      setApplications([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 进入审批台时先做一次超时巡检（提醒 / 自动升级），再拉取最新数据。
      try {
        await invokeApprovals({ action: "checkTimeouts" });
      } catch {
        // 巡检失败不阻塞列表加载
      }
      const data = await invokeApprovals<{
        workflows?: ApprovalWorkflow[];
        requests?: ApprovalRequest[];
        decisions?: ApprovalDecision[];
        applications?: ApprovalApplication[];
      }>({ action: "list" });
      setWorkflows(data.workflows ?? []);
      setRequests(data.requests ?? []);
      setDecisions(data.decisions ?? []);
      setApplications(data.applications ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "审批数据加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveWorkflow = useCallback(
    async (input: WorkflowInput) => {
      await invokeApprovals({ action: "saveWorkflow", ...input });
      await load();
    },
    [load],
  );

  const deleteWorkflow = useCallback(
    async (workflowId: number) => {
      await invokeApprovals({ action: "deleteWorkflow", workflowId });
      await load();
    },
    [load],
  );

  const startRequest = useCallback(
    async (applicationId: number) => {
      await invokeApprovals({ action: "startRequest", applicationId });
      await load();
    },
    [load],
  );

  const decide = useCallback(
    async (requestId: number, decision: "approved" | "rejected", note: string) => {
      await invokeApprovals({ action: "decide", requestId, decision, note });
      await load();
    },
    [load],
  );

  const checkTimeouts = useCallback(async () => {
    const result = await invokeApprovals<{ reminded?: number; escalated?: number }>({ action: "checkTimeouts" });
    return { reminded: result.reminded ?? 0, escalated: result.escalated ?? 0 };
  }, []);

  return {
    workflows,
    requests,
    decisions,
    applications,
    loading: loading || authLoading,
    error,
    reload: load,
    saveWorkflow,
    deleteWorkflow,
    startRequest,
    decide,
    checkTimeouts,
  };
}